import { Component, Input, Output, EventEmitter, Injector, ViewChild } from '@angular/core';
import type { OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { NgForm } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

// Note: Do NOT import from @shared/pages to avoid circular dependency
// This component should be self-contained
import type { TCustomer } from '@shared/models';
import { SessionService, VariableReplacementService } from '@shared/services';
import type { CustomerMessageData, CustomerMessageConfig } from './customer-messages.types';

@Component({
    selector: 'app-customer-messages',
    templateUrl: './customer-messages.component.html',
    styleUrls: ['./customer-messages.component.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule, IonicModule]
})
export class CustomerMessagesComponent implements OnInit, OnChanges {
    @ViewChild('f') public form: NgForm;
    
    @Input() public config: CustomerMessageConfig;
    @Input() public data: CustomerMessageData;
    @Input() public submitted = false;
    @Output() public dataChange = new EventEmitter<CustomerMessageData>();
    @Output() public validChange = new EventEmitter<boolean>();

    // Services needed (previously inherited from base class)
    protected sessionService: SessionService;
    protected variableReplacementService: VariableReplacementService;
    protected elem: HTMLTextAreaElement;

    constructor(injector: Injector) {
        this.sessionService = injector.get(SessionService);
        this.variableReplacementService = injector.get(VariableReplacementService);
    }

    public async ngOnInit(): Promise<void> {
        // Initial message computation if data exists
        if (this.data?.rawMessage) {
            await this.computeRenderedMessage();
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        // React to data changes from parent
        if (changes['data'] && !changes['data'].firstChange) {
            if (this.data?.rawMessage) {
                this.computeRenderedMessage();
            }
        }
    }

    public onEnabledChange(): void {
        if (!this.data) {
            return;
        }
        
        if (this.data.enabled) {
            this.data.enabledStr = this.config.enabledLabel;
        } else {
            this.data.enabledStr = this.config.disabledLabel;
        }
        
        this.dataChange.emit(this.data);
    }

    public async onKeyUp(event: any): Promise<void> {
        this.elem = event.srcElement as HTMLTextAreaElement;
    }

    public async onMessageChange(): Promise<void> {
        await this.computeRenderedMessage();
        const numChars = this.data.renderedMessage?.length || 0;
        const numParts = Math.ceil(numChars / 160);
        this.data.message = 'Chars: ' + numChars + '.  Parts: ' + numParts;
        
        this.dataChange.emit(this.data);
    }

    public onRangeChange(): void {
        this.computeRenderedMessage();
        this.dataChange.emit(this.data);
    }

    private async computeRenderedMessage(): Promise<void> {
        if (!this.data) {
            return;
        }

        const rawMessage = this.data.rawMessage;
        if (!rawMessage) {
            this.data.renderedMessage = '';
            return;
        }

        await this.variableReplacementService.reset();

        // Set customer for variable replacement
        const customer: TCustomer = this.sessionService.getFakeCustomer();
        this.variableReplacementService.customer = customer;

        // Set range slider values if present
        if (this.config.hasRangeSlider && this.config.rangeConfig) {
            const propertyName = this.config.rangeConfig.propertyName;
            const value = this.data[propertyName];
            
            if (propertyName === 'numMinutes') {
                this.variableReplacementService.numMinutes = value;
            } else if (propertyName === 'numMilesToGo') {
                this.variableReplacementService.numMilesToGo = value;
            }
        }

        const renderedMessage = this.variableReplacementService.doVariableReplacements(rawMessage);
        this.data.renderedMessage = renderedMessage;
    }
}
